import type { HeroMediaItem } from "@/features/store-editor/records";
import type { PreviewMediaItem } from "../content/preview-details.types";
const maxMediaItems = 8;
function heroMediaItemToPreview(item: HeroMediaItem): PreviewMediaItem {
  return item.kind === "video_url"
    ? {
        kind: "highlight",
        src: item.src,
        alt: item.alt,
        poster: item.poster ?? null,
      }
    : { kind: "capture", src: item.src, alt: item.alt };
}
export function composeOverrideMedia({
  heroMediaOverride,
  showLiveReplay,
  replay,
  replayUnfit,
}: {
  heroMediaOverride: readonly HeroMediaItem[];
  showLiveReplay: boolean;
  replay: PreviewMediaItem | null;
  replayUnfit: boolean;
}): PreviewMediaItem[] {
  const creatorItems = [...heroMediaOverride]
    .toSorted((a, b) => a.order - b.order)
    .map(heroMediaItemToPreview);
  const wantsReplay = showLiveReplay && replay !== null;
  const ordered =
    wantsReplay && replay
      ? replayUnfit
        ? [...creatorItems, replay]
        : [replay, ...creatorItems]
      : creatorItems;
  const seen = new Set<string>();
  const media: PreviewMediaItem[] = [];

  for (const item of ordered) {
    if (media.length >= maxMediaItems) break;

    if (seen.has(item.src)) continue;

    seen.add(item.src);
    media.push(item);
  }

  return media;
}

export function composePreviewMedia({
  replay,
  replayUnfit: unfit,
  screenshots,
  illustrations,
}: {
  readonly replay: PreviewMediaItem | null;
  readonly replayUnfit: boolean;
  readonly screenshots: readonly PreviewMediaItem[];
  readonly illustrations: readonly PreviewMediaItem[];
}): PreviewMediaItem[] {
  const seen = new Set<string>();
  const media: PreviewMediaItem[] = [];
  const appendMedia = ({
    items,
    maxItems,
  }: {
    items: readonly PreviewMediaItem[];
    maxItems: number;
  }) => {
    for (const item of items) {
      if (media.length >= maxItems) break;

      if (seen.has(item.src)) continue;

      seen.add(item.src);
      media.push(item);
    }
  };

  if (replay && !unfit) {
    seen.add(replay.src);
    media.push(replay);
  }

  appendMedia({
    items: screenshots,
    maxItems: maxMediaItems - (replay && !seen.has(replay.src) ? 1 : 0),
  });

  if (replay && unfit && !seen.has(replay.src)) {
    seen.add(replay.src);
    media.push(replay);
  }

  appendMedia({ items: illustrations, maxItems: maxMediaItems });
  return media;
}
